import { RASCUNHO_VAZIO, type Destinatario, type Rascunho } from './rascunho';
import type { MensagemDaApi } from './api';

/**
 * Monta o rascunho de uma resposta ou encaminhamento.
 *
 * Função pura de propósito: é aqui que moram os erros clássicos de cliente de
 * e-mail - responder a todos incluindo você mesmo, empilhar `Re: Re: Re:`,
 * responder para o `From` quando existe `Reply-To`, perder o `In-Reply-To` e
 * quebrar a conversa na caixa de quem recebe.
 */

export type TipoDeResposta = 'responder' | 'responder_a_todos' | 'encaminhar';

/**
 * Prefixos que já significam resposta ou encaminhamento, em pt-BR e em inglês.
 * Sem isto o assunto vira `Re: Re: Res: Fwd: ...` depois de três trocas.
 */
const PREFIXO_RESPOSTA = /^\s*(re|res|rec|reply)\s*(\[\d+\])?\s*:\s*/i;
const PREFIXO_ENCAMINHAMENTO = /^\s*(enc|encaminhada|fw|fwd|encaminhar)\s*:\s*/i;

export function assuntoDeResposta(assunto: string | null): string {
  const base = (assunto ?? '').replace(PREFIXO_RESPOSTA, '').trim();
  // `trim` no fim: mensagem sem assunto viraria `Re: ` com espaco solto, e esse
  // espaco iria no cabecalho da resposta.
  return `Re: ${base}`.trim();
}

export function assuntoDeEncaminhamento(assunto: string | null): string {
  const base = (assunto ?? '').replace(PREFIXO_ENCAMINHAMENTO, '').trim();
  return `Enc: ${base}`.trim();
}

function mesmoEndereco(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** Remove repetidos e os endereços do próprio usuário. */
function limpar(lista: Destinatario[], proprios: string[]): Destinatario[] {
  const vistos = new Set<string>();
  const saida: Destinatario[] = [];

  for (const d of lista) {
    const chave = d.address.trim().toLowerCase();
    if (chave === '' || vistos.has(chave)) continue;
    if (proprios.some((meu) => mesmoEndereco(meu, d.address))) continue;
    vistos.add(chave);
    saida.push(d);
  }

  return saida;
}

function citar(m: MensagemDaApi, corpoHtml: string, acima: boolean): string {
  const quem = m.from?.name !== '' && m.from != null ? m.from.name : (m.from?.address ?? 'alguém');
  const quando = new Date(m.sentAt).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  // `blockquote` e não texto com `>`: o composer é rich text, e a citação
  // precisa poder ser recolhida por quem lê depois. O `div[data-citacao]` é o
  // que diz à assinatura onde a citação começa (ADR 0044).
  const citacao = [
    '<div data-citacao="">',
    `<p>Em ${escapar(quando)}, ${escapar(quem)} escreveu:</p>`,
    `<blockquote>${corpoHtml}</blockquote>`,
    '</div>',
  ].join('');
  // O parágrafo vazio é onde a pessoa escreve: acima da citação, como o
  // Outlook, ou abaixo, como nas listas de discussão.
  return acima ? `<p><br></p>${citacao}` : `${citacao}<p><br></p>`;
}

/**
 * O corpo citado entra no editor como HTML. O que vem da mensagem original já
 * passou pelo sanitizador da API, mas o nome e a data vêm de campos que não
 * passaram - então escapa.
 */
function escapar(texto: string): string {
  return texto
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export interface OpcoesDeResposta {
  tipo: TipoDeResposta;
  mensagem: MensagemDaApi;
  /** HTML já sanitizado do corpo original, para a citação. */
  corpoHtml: string;
  /** Endereços das caixas do usuário: nunca respondem para si mesmas. */
  meusEnderecos: string[];
  /** Identidade que assina a resposta. */
  identidade: string;
  /** `redacao.responderAcima` das preferências. Ausente, acima. */
  responderAcima?: boolean;
}

export function rascunhoDeResposta(opcoes: OpcoesDeResposta): Rascunho {
  const { tipo, mensagem: m, corpoHtml, meusEnderecos, identidade } = opcoes;
  const acima = opcoes.responderAcima ?? true;

  if (tipo === 'encaminhar') {
    return {
      ...RASCUNHO_VAZIO,
      identidade,
      subject: assuntoDeEncaminhamento(m.subject),
      // Encaminhar não herda destinatário: quem encaminha escolhe para quem.
      html: citar(m, corpoHtml, acima),
      citando: true,
      // Nem a costura: encaminhamento abre outra conversa, não continua esta.
      //
      // Os anexos vão por referência. Encaminhar sem os arquivos é perder
      // silenciosamente o que costuma ser o motivo do encaminhamento - e trazer
      // os bytes para o navegador só para devolvê-los seria trabalho em dobro.
      anexos: m.attachments
        .filter((a) => !a.isInline)
        .map((a) => ({
          id: `origem-${a.id}`,
          filename: a.filename,
          sizeBytes: a.sizeBytes,
          contentType: a.contentType,
          // Já está pronto: o conteúdo existe, só não aqui.
          progresso: 100,
          origem: { messageId: m.id, anexoId: a.id },
        })),
    };
  }

  // `Reply-To` vence o `From` quando existe - é para isso que ele serve.
  const destino = m.replyTo.length > 0 ? m.replyTo : m.from !== null ? [m.from] : [];
  const to = limpar(destino, meusEnderecos);

  const cc =
    tipo === 'responder_a_todos'
      ? limpar(
          [...m.to, ...m.cc],
          // Além dos meus endereços, tira quem já está no `to` da resposta.
          [...meusEnderecos, ...to.map((d) => d.address)],
        )
      : [];

  return {
    ...RASCUNHO_VAZIO,
    identidade,
    to,
    cc,
    subject: assuntoDeResposta(m.subject),
    html: citar(m, corpoHtml, acima),
    citando: true,
    emRespostaA: m.rfcMessageId,
    // A cadeia cresce: as referências da original mais ela própria.
    referencias: m.rfcMessageId === null ? m.references : [...m.references, m.rfcMessageId],
  };
}
